import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
} from '@mui/material'
import { ReactElement } from 'react'
import TruncatedText from './TruncatedText'

interface CheckboxInputProps {
    id: string
    label: string
    value: boolean
    disabled?: boolean
    onChange: (id: string, value: boolean) => void
    labelStyels?: any
    errorMessage?: string
    helperText?: string
    // Forces the label to actually shrink/ellipsis inside a constrained flex row (see
    // OrgPickerModal's Available list) instead of the default content-based sizing that lets
    // FormControlLabel grow past its column — only opt in where the container is genuinely
    // width-constrained, since it also makes short labels compute a spuriously small clientWidth
    // in unrelated nested-flex contexts (e.g. TemplateHierarchy's indented tree rows).
    truncateLabel?: boolean
}

const CheckboxInput = (props: CheckboxInputProps): ReactElement => {
    const {
        id,
        label,
        value,
        disabled,
        onChange,
        labelStyels,
        errorMessage,
        helperText,
        truncateLabel = false,
    } = props
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange(id, event.target.checked)
    }

    return (
        <Box sx={{ width: '100%', ...(truncateLabel && { minWidth: 0 }) }}>
            <FormControl
                sx={truncateLabel ? { width: '100%', minWidth: 0 } : undefined}
            >
                <FormControlLabel
                    label={
                        <Box
                            sx={{
                                width: '100%',
                                ...(truncateLabel && { minWidth: 0 }),
                            }}
                        >
                            <TruncatedText
                                sx={{
                                    fontSize: '14px',
                                    ...labelStyels,
                                }}
                                text={null}
                            >
                                {label}
                            </TruncatedText>
                        </Box>
                    }
                    sx={{
                        width: 'calc(100% - 20px)',
                        ...(truncateLabel && {
                            minWidth: 0,
                            '& .MuiFormControlLabel-label': {
                                minWidth: 0,
                                overflow: 'hidden',
                                width: '100%',
                            },
                        }),
                    }}
                    control={
                        <Checkbox
                            id={id}
                            checked={value}
                            onChange={handleChange}
                            slotProps={{
                                input: {
                                    'aria-label': 'controlled',
                                },
                            }}
                            sx={{
                                padding: '0px 9px',
                                // '& .MuiSvgIcon-root': { fontSize: 28 },
                            }}
                            size="small"
                            disabled={disabled}
                        />
                    }
                />
                {errorMessage && (
                    <FormHelperText sx={{ color: 'red', margin: '2px' }}>
                        {errorMessage || ''}
                    </FormHelperText>
                )}
                {helperText && (
                    <FormHelperText
                        sx={{
                            color: 'text.secondary',
                            margin: '2px',
                            fontSize: '12px',
                        }}
                    >
                        {helperText}
                    </FormHelperText>
                )}
            </FormControl>
        </Box>
    )
}

export default CheckboxInput
